<template>
	<DxLoadPanel v-if="isLoading" :position="{ of: '.primary-scrollview' }" :visible="true" :show-indicator="true" :show-pane="true" shading-color="rgba(0,0,0,0.4)" />
	<div v-if="fatalError" class="content-block dx-error-message">
		<i class="dx-icon-warning"></i>
		<span class="dx-error-message-text">{{ fatalError }}</span>
	</div>
	<div class="page-render" v-else-if="page">
		<h2 class="content-block page-render-title">{{ page.title || 'Unknown Page' }}<span v-if="page.context" class="context-title">({{ page.context.moteTitle() }})</span></h2>
		<page-render-cell v-if="page.layout && page.layout.type" :cell="page.layout" :page="page" />
	</div>
</template>

<style lang="scss">
.page-render > .render-cell-splitter {
	height: calc(100vh - 150px);
}
.page-render-title .context-title {
	padding-left: 8px;
	color: #999;
}
</style>

<script setup>
import { ref, watch, inject } from 'vue';
import PageRenderCell from '../components/page-render-cell';
import { DxLoadPanel } from 'devextreme-vue/load-panel';
import { useRoute } from 'vue-router';
import Page from '@/pages';
import AuthSystem from '@/auth';
import { isUserAllowedOnPage } from '../services/permissions';

const $http = inject('$http');
const route = useRoute();
const isLoading = ref(true);			// If we are loading any critical layout data, we can't show anything but a placeholder.
const fatalError = ref(null);			// If we have a fatal error from loading or a component, we override everything with an error message.
const page = ref(null);					// Page object instantiated from server data, or null if not loaded. Consists of layout, settings, config, and other page-specific data, including optional context object.

// Watch for route changes (and also immediately once mounted) to fetch and render the page layout.
watch( () => [route.params.groupRef, route.params.pageRef, route.params.contextId],
	async ([newGroupRef, newPageRef, newContextId]) => {
		if (newGroupRef && newPageRef) {
			await fetchPageLayout(newGroupRef, newPageRef, newContextId);
		}
	},
	{ immediate: true }
);

// Given a groupRef (group ID/slug) and pageRef (page ID/slug), and optional contextId, fetch the layout and settings for the page.
// TODO: Perhaps we can use a group-based caching model so we don't need to fetch this all the time, but ensure we send a API request to check cache validity and force reload when needed.
async function fetchPageLayout(groupRef, pageRef, contextId) {
	isLoading.value = true;
	fatalError.value = null;			// Always reset our previous fatal error, since the way we exit an error state is to navigate elsewhere.

	try {
		const validFormat = /^[\w-]+$/;
		if (!validFormat.test(groupRef) || !validFormat.test(pageRef)) {
			throw new Error('Invalid group or page reference format');
		}
		if (contextId) {
			const contextIdNum = parseInt(contextId, 10);
			if (isNaN(contextIdNum) || contextIdNum <= 0) {
				throw new Error('Invalid context ID format');
			}
		}
		
		let apiUrl = `/group-page/${groupRef}/${pageRef}` + (contextId ? `/${contextId}` : '');
		// Note: context is returned as a mote object in the page data response. Our page object constructor will store it safely - uncached.
		const response = await $http.get(apiUrl);
		page.value = new Page(response.data);
		// Check that context is valid for the settings, if applicable. If not, show an error instead of rendering the page.
		if (page.value.shouldUseContext() && !page.value.hasValidContext()) {
			throw new Error('Invalid context for page');
		}
        // Enforce page-level permissions if enabled in settings
        const userResult = await AuthSystem.getUser();
        const user = userResult?.data || null;
        const isAdmin = !!(AuthSystem.isAdmin && AuthSystem.isAdmin.value);
        if (!isUserAllowedOnPage(page.value, user, isAdmin)) {
            throw new Error('You do not have access to this page');
        }
		console.log("Page layout fetched:", page.value);
	} catch (err) {
		fatalError.value = err.message;
		console.error('Error fetching page layout:', err);
	} finally {
		isLoading.value = false;
	}
}

</script>
